import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';


// je-search-normalise-20260825: canonicalise only spellings and compound forms
// customers actually used. This improves recall without replacing Shopify's
// relevance order or broadly expanding a query to merely related products.
const normalizeJohnsSearchTerm = (value) =>
  value
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\bdrier\b/gi, 'dryer')
    .replace(/\bstero\b/gi, 'stereo')
    .replace(/\bsound[\s-]+bars?\b/gi, 'soundbar')
    .replace(/\bunder[\s-]+counter\b/gi, 'undercounter')
    .replace(/\bair\s*con(?:ditioner|ditioning)?s?\b/gi, 'air conditioning')
    .replace(/\bamerican\s+fridge(?![\s-]+freezer\b)/gi, 'american fridge freezer');

// je-search-core-20260903: the same index the modal uses (see assets/predictive-search.js).
function jeSearchLoad(host) {
  const core = host && host.dataset ? host.dataset.jeCore : null;
  const url = host && host.dataset ? host.dataset.jeIndex : null;
  if (!core || !url) return Promise.resolve(null);
  return import(core).then((mod) => mod.loadSearch(url)).catch(() => null);
}

export function jeQolWaitFor(promise, timeoutMs = 5000) {
  let timer = 0;
  const timeout = new Promise((resolve) => {
    timer = window.setTimeout(() => resolve({ timedOut: true, value: null }), timeoutMs);
  });
  const settled = Promise.resolve(promise).then((value) => ({ timedOut: false, value }));
  return Promise.race([settled, timeout]).finally(() => {
    window.clearTimeout(timer);
  });
}

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.closest('form')?.addEventListener('submit', this.#normalizeBeforeSubmit, {
      signal: this.#controller.signal,
    });
    this.#jeRestore();
    this.#jeLoadDirectQuery();
    this.refs.searchPageInput.addEventListener('focus', () => {
      jeSearchLoad(this).then((je) => { this.#je = je; });
    }, { once: true, signal: this.#controller.signal });
  }

  // ---- je-search-core-20260903 -------------------------------------------------------
  // An id-list URL is the matcher's route: show the customer's own words in the box and the
  // heading, order the grid by the matcher's ranking (Shopify returns an id: query in its own
  // order) and keep that order as infinite scroll appends pages.
  #je = null;
  #jeSortTimer = 0;
  #jeGateToken = 0;

  #jeSetRawSearchLoading(active) {
    const gate = window.JEQolRawSearchGate;
    const list = document.querySelector('results-list');
    if (active) {
      list?.setAttribute('aria-busy', 'true');
      document.documentElement.classList.add('je-qol-search-pending');
      return;
    }
    list?.removeAttribute('aria-busy');
    if (gate && typeof gate.clear === 'function') gate.clear();
    else document.documentElement.classList.remove('je-qol-search-pending');
  }

  // je-search-requirements-20261010: native search is candidate retrieval only.
  // Nothing becomes visible until the shared matcher has approved its exact ID.
  #jeObserver = null;
  #jeValidatedUrl = '';

  async #jeLoadDirectQuery(prepared = null) {
    const params = new URLSearchParams(window.location.search);
    const raw = (params.get('q') || '').trim();
    const term = (params.get('je_q') || raw).trim();
    const token = ++this.#jeGateToken;
    this.#jeObserver?.disconnect();
    this.#jeSetRawSearchLoading(true);
    const status = document.querySelector('[data-je-qol-search-page-status]');
    const fail = () => {
      if (status) {
        status.hidden = false;
        status.innerHTML = 'We could not check your search. <a href="' + this.#jeEscape(window.location.href) + '">Try again</a>.';
      }
      document.querySelector('results-list')?.removeAttribute('aria-busy');
    };
    if (!term) {
      if (status) status.textContent = 'Enter a product type, model or the size you need.';
      return;
    }
    try {
      // Reuse only the match that requested this exact fresh server-rendered ID URL.
      const reused = prepared && prepared.term === term && prepared.expected === raw && params.has('je_q') ? prepared : null;
      const loaded = reused ? { value: reused.je, timedOut: false } : await jeQolWaitFor(jeSearchLoad(this));
      if (token !== this.#jeGateToken) return;
      const je = loaded.value;
      if (!je || loaded.timedOut) { fail(); return; }
      this.#je = je;
      const res = reused ? reused.res : je.search(term, { limit: 120, ghostLimit: 6 });
      if (!res.total) {
        this.#jeShowNoMatch(term, je);
        if (status) status.hidden = true;
        return;
      }
      // Raw OR results may omit actual matches as well as add incorrect ones. Request
      // the matcher IDs before displaying anything; keep the shopper's sort/filters.
      const expected = res.ids.map((id) => 'id:' + id).join(' OR ');
      if (raw !== expected || !params.has('je_q')) {
        params.set('q', expected);
        params.set('je_q', term);
        params.set('type', 'product');
        params.delete('page');
        params.delete('je_no_match');
        const next = '/search?' + params.toString();
        if (await this.#jeSwapInPlace(next, token, { je, res, term, expected })) return;
        if (token !== this.#jeGateToken) return;
        window.location.replace(next);
        return;
      }
      const allowed = new Set(res.ids.map(String));
      this.#jeValidatedUrl = window.location.href;
      const validateCards = () => {
        if (window.location.href !== this.#jeValidatedUrl) {
          this.#jeLoadDirectQuery();
          return;
        }
        const list = document.querySelector('results-list');
        if (!list) return;
        // je-search-inputs-20261011: say what the matcher did with the words, above the grid
        if (res.notice && !document.querySelector('[data-je-search-notice]')) {
          const grid = list.querySelector('.collection-wrapper');
          if (grid) grid.insertAdjacentHTML('beforebegin', '<p class="je-search-page-none" data-je-search-notice role="status">'
            + this.#jeEscape(res.notice) + '</p>');
        }
        list.querySelectorAll('li[data-product-id]').forEach((card) => {
          if (allowed.has(card.dataset.productId)) card.setAttribute('data-je-search-approved', '');
          else card.remove();
        });
        const wrapper = list.querySelector('.collection-wrapper');
        if (wrapper) {
          // Prioritize only already approved first-row art, keeping exact native sources/effects.
          const firstRow = window.matchMedia('(max-width:749px)').matches ? 2 : 4;
          Array.from(wrapper.querySelectorAll('li[data-je-search-approved]')).slice(0, firstRow).forEach(card => {
            const image = card.querySelector('img.je-ped-art') || card.querySelector('img');
            if (image) { image.loading = 'eager'; image.fetchPriority = 'high'; }
          });
          wrapper.setAttribute('data-je-search-verified', '');
          wrapper.hidden = false;
          wrapper.style.removeProperty('display');
        }
        const currentStatus = list.querySelector('[data-je-qol-search-page-status]');
        if (currentStatus) currentStatus.hidden = true;
        list.removeAttribute('aria-busy');
      };
      validateCards();
      this.#jeSetRawSearchLoading(false);
      document.querySelector('results-list')?.resumeSearchPagination?.();
      this.#jeObserver = new MutationObserver(validateCards);
      this.#jeObserver.observe(document.querySelector('main') || document.body, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['data-product-id']
      });
    } catch (error) {
      if (token === this.#jeGateToken) fail();
    }
  }

  // je-search-in-place-20261016: load the matcher's id list into the results section without a second page
  // load (Will, 16 Oct 2026: "9 fix it"). The address becomes the id URL (replaceState, so Back is unchanged),
  // the results section is re-rendered for it and morphed in place, then this component checks the cards again.
  // Returns false (and puts the address back) whenever that cannot be done, so the caller reloads as before.
  async #jeSwapInPlace(next, token, prepared) {
    const section = document.querySelector('results-list')?.closest('.shopify-section');
    if (!section || !section.id || !window.history || typeof window.history.replaceState !== 'function') return false;
    const before = window.location.href;
    try {
      const { sectionRenderer } = await import('@theme/section-renderer');
      if (token !== this.#jeGateToken) return true;
      window.history.replaceState(window.history.state, '', next);
      // The results list cached page 2 of the first search; tell it the query changed, the way a filter does,
      // so it drops that and watches its next-page marker again once the new grid is in (reviewer, 16 Oct).
      const { FilterUpdateEvent } = await import('@theme/events');
      document.dispatchEvent(new FilterUpdateEvent(new URLSearchParams(window.location.search)));
      const done = await jeQolWaitFor(sectionRenderer.renderSection(section.id.replace(/^shopify-section-/, ''), { cache: false }), 8000);
      // A failed render (a 429 page, say) has no results section in it, and Horizon's morph would then throw
      // unseen and leave the old cards under the new address, so check the response itself.
      if (done.timedOut || typeof done.value !== 'string' || !done.value.includes('id="' + section.id + '"')) throw new Error('results render failed');
      if (token !== this.#jeGateToken) return true;
      if (!document.querySelector('results-list')) throw new Error('results list missing after render');
      const list = document.querySelector('results-list');
      // je-search-art-rearm-20261027: the morph puts each card's class back to the server's copy, dropping
      // je-ped-live from a card whose art had already loaded. That img never loads again, so its onload cannot
      // re-arm the card and the photo stayed hidden (blank Fridgemaster MTL55242E card, 19 Oct 2026).
      list.querySelectorAll('img.je-ped-art').forEach((img) => {
        if (img.complete && img.naturalWidth > 0) img.closest('product-card')?.classList.add('je-ped-live');
      });
      list.pages?.clear?.(); // a page-2 prefetch still in flight
      // Watch the next-page marker afresh: observe() always reports once, so if the marker is already inside
      // the load zone the id list's page 2 is fetched now (otherwise infinite scroll could stall; reviewer).
      list.updatedCallback?.();
      const marker = list.refs?.viewMoreNext;
      if (marker && list.infinityScrollObserver) {
        list.infinityScrollObserver.unobserve(marker);
        list.infinityScrollObserver.observe(marker);
      }
      this.#jeRestore();
      this.#jeLoadDirectQuery(prepared);
      return true;
    } catch (error) {
      if (window.location.href !== before) window.history.replaceState(window.history.state, '', before);
      return false;
    }
  }

  #jeShowNoMatch(term, je) {
    const wrapper = document.querySelector('results-list .collection-wrapper');
    if (!wrapper || document.querySelector('[data-je-search-no-match]')) return;
    wrapper.hidden = true;
    const matched = je.search(term, { ghostLimit: 1000 });
    if (matched.ghostTotal) {
      // Exact matched rows only. Do not forward the requirements to the partner's text filter.
      const items = matched.ghosts.map((p) => '<li><a href="/pages/euronics-partner?q='
        + encodeURIComponent(p.s || (p.k || [])[0] || '') + '">'
        + this.#jeEscape(p.n || p.t) + '</a></li>').join('');
      wrapper.insertAdjacentHTML('beforebegin',
        '<section class="je-search-page-none" data-je-search-no-match data-je-search-partner aria-live="polite">'
        + '<p>Matching products available to order</p><ul>' + items + '</ul></section>');
      document.title = 'Matching products | Johns Electrical';
      return;
    }
    document.title = 'No products found for "' + term + '" | Johns Electrical';
    // je-search-inputs-20261011: a help question ("delivery", "opening times") gets its page
    if (matched.pages && matched.pages.length) {
      const links = matched.pages.map((p) => '<li><a href="' + this.#jeEscape(p.u) + '">' + this.#jeEscape(p.t) + '</a></li>').join('');
      wrapper.insertAdjacentHTML('beforebegin',
        '<section class="je-search-page-none" data-je-search-no-match data-je-search-help aria-live="polite">'
        + '<p>This should answer it:</p><ul>' + links + '</ul>'
        + '<p>Or call the shop on <a href="tel:01724844040">01724 844040</a>.</p></section>');
      return;
    }
    wrapper.insertAdjacentHTML('beforebegin',
      '<section class="je-search-page-none" data-je-search-no-match aria-live="polite">'
      + '<p>No products match all the details in &ldquo;' + this.#jeEscape(term) + '&rdquo;.</p>'
      + '<p>For a minimum capacity, try &ldquo;at least 200L freezer&rdquo;. You can also search by product type or model.</p>'
      + '<p>Need help finding one? Call <a href="tel:01724844040">01724 844040</a>.</p>'
      + '</section>');
  }

  #jeEscape(value) {
    return String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  #jeRestore() {
    let params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    const jeQ = params.get('je_q');
    const q = params.get('q') || '';
    if (jeQ) {
      this.refs.searchPageInput.value = jeQ;
      // The browser tab and screen-reader heading should use the shopper's words too.
      document.title = 'Search results for “' + jeQ + '” – Johns Electrical, Scunthorpe';
      const accessibleHeading = document.querySelector('results-list h1.visually-hidden');
      if (accessibleHeading) accessibleHeading.textContent = 'Search results for “' + jeQ + '”';
      const h = document.querySelector('.search-page__header h3');
      if (h && !h.dataset.jeQ) {
        h.dataset.jeQ = '1';
        h.textContent = 'Search results for “' + jeQ + '”';
      }
    }
    if (!/^id:\d+/.test(q)) return;
    const order = new Map();
    q.split(' OR ').forEach((part, i) => {
      const m = part.match(/(\d+)/);
      if (m && !order.has(m[1])) order.set(m[1], i);
    });
    const sortGrid = () => {
      const first = document.querySelector('results-list li[data-product-id]');
      const grid = first && first.parentElement;
      if (!grid) return;
      const items = Array.from(grid.children).filter((el) => el.matches && el.matches('li[data-product-id]'));
      const ranked = items.map((el, i) => ({ el, r: order.has(el.dataset.productId) ? order.get(el.dataset.productId) : 1e9 + i }));
      ranked.sort((a, b) => a.r - b.r);
      if (ranked.every((x, i) => x.el === items[i])) return;
      ranked.forEach((x) => grid.appendChild(x.el));
    };
    sortGrid();
    const target = document.querySelector('results-list') || document.body;
    new MutationObserver(() => {
      clearTimeout(this.#jeSortTimer);
      this.#jeSortTimer = setTimeout(sortGrid, 60);
    }).observe(target, { childList: true, subtree: true });
  }
  // ---- end je-search-core-20260903 ---------------------------------------------------

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#jeObserver?.disconnect();
    ++this.#jeGateToken;
  }

  #normalizeBeforeSubmit = (event) => {
    const input = this.refs.searchPageInput;
    // je-search-core-20260903: route through the matcher when it has already loaded (it starts
    // loading on focus; a submit must never wait on the network, so an unloaded index simply
    // lets Shopify's own search run)
    if (this.#je && input.value.trim()) {
      const res = this.#je.search(input.value.trim(), { limit: 1 });
      if (res && res.route && res.route.url) {
        if (event) event.preventDefault();
        window.location.href = res.route.url;
        return;
      }
    }
    const normalized = normalizeJohnsSearchTerm(input.value);
    if (normalized) input.value = normalized;
  };

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
